import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/button';
import {
  createMaxDecimalDigitsValidator,
  TextInput,
  validationFunctions,
} from '@/components/text-input';
import { createStyles } from '@/contexts/theme-context';
import {
  formatPriceInMicrosWithoutSymbol,
  getCurrencySymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { TextStyle, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface Props {
  currencyCode: string | null;
  initialAmountInMicros?: number | null;
  invoiceTotalInMicros: number;
  onClear?: () => void;
  onSave: (amountInMicros: number) => void;
}

const AMOUNT_VALIDATORS = [
  validationFunctions.DECIMAL,
  createMaxDecimalDigitsValidator(0, 2),
];

const parseAmountToMicros = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.round(parsed * MICRO_CONVERSION);
};

export const PartialPaymentScreenComponent: React.FC<Props> = ({
  currencyCode,
  initialAmountInMicros,
  invoiceTotalInMicros,
  onClear,
  onSave,
}) => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const { bottom: safeBottom } = useSafeAreaInsets();
  const hasInitialPartial =
    initialAmountInMicros !== null && initialAmountInMicros !== undefined;
  const showClearButton = hasInitialPartial && onClear !== undefined;

  const initialAmount = useMemo(() => {
    if (!hasInitialPartial) {
      return '';
    }
    return formatPriceInMicrosWithoutSymbol(
      initialAmountInMicros!,
      currencyCode,
    );
  }, [currencyCode, hasInitialPartial, initialAmountInMicros]);

  const [amountText, setAmountText] = useState<string>(initialAmount);
  const parsedAmountInMicros = useMemo(
    () => parseAmountToMicros(amountText),
    [amountText],
  );
  const isValidAmount =
    parsedAmountInMicros !== null &&
    parsedAmountInMicros > 0 &&
    parsedAmountInMicros <= invoiceTotalInMicros;

  const handleSubmit = useCallback(() => {
    if (!isValidAmount || parsedAmountInMicros === null) {
      return;
    }
    onSave(parsedAmountInMicros);
  }, [isValidAmount, onSave, parsedAmountInMicros]);

  return (
    <View
      style={[
        styles.container,
        isSmallLayout ? styles.containerMobile : styles.containerDesktop,
      ]}
    >
      <View style={styles.formContent}>
        <TextInput
          accessibilityLabel={i18n.t('partial_payment.amount_label')}
          containerStyle={styles.input}
          focusedBorderColor={theme.sk.color.border.input.active}
          keyboardType="decimal-pad"
          label={i18n.t('partial_payment.amount_label')}
          labelStyle={theme.sk.typography.label.md as TextStyle}
          leftElement={
            <Typography
              color={theme.sk.color.text.hint}
              variant="paragraph-base"
            >
              {getCurrencySymbol(currencyCode)}
            </Typography>
          }
          onChangeText={setAmountText}
          selectionColor={theme.sk.color.border.input.active}
          showDoneButtonAccessoryView
          useOuterLabel
          validators={AMOUNT_VALIDATORS}
          value={amountText}
        />
      </View>
      <View
        style={
          isSmallLayout
            ? [
                styles.buttonContainerMobile,
                {
                  paddingBottom: Math.max(
                    safeBottom,
                    theme.sk.spacing.spaceMedium,
                  ),
                },
              ]
            : styles.buttonContainerDesktop
        }
      >
        {showClearButton ? (
          <Button
            accessibilityLabel={i18n.t('clear')}
            onPress={onClear}
            trackingData={{ name: 'partial_payment.clear_button' }}
            type="danger"
          >
            {i18n.t('clear')}
          </Button>
        ) : null}
        <Button
          accessibilityLabel={i18n.t('save')}
          disabled={!isValidAmount}
          onPress={handleSubmit}
          trackingData={{ name: 'partial_payment.save_button' }}
        >
          {i18n.t('save')}
        </Button>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  buttonContainerDesktop: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'flex-end',
    marginTop: theme.sk.spacing.spaceLarge,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  buttonContainerMobile: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  containerDesktop: {
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  containerMobile: {
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  formContent: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  input: {
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
  },
}));
